import { inject, Service } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { logging } from '@utils/logging';
import { getActiveLanguage } from '@utils/translate';
import { MatomoRouteInterceptorBase, MatomoTracker } from 'ngx-matomo-client';

/**
 * Custom Matomo route interceptor that filters out sensitive data from the URL
 * and sets custom dimensions based on user roles and language
 * NB! This interceptor registered separately in the app module, inside the `provideMatomo` function
 */
@Service({ autoProvided: false })
export class MatomoInterceptor extends MatomoRouteInterceptorBase<string> {
  private readonly tracker = inject(MatomoTracker);
  private readonly translaionService = inject(TranslateService);
  private readonly paramFilters = ['cardId', 'id'];

  protected extractRouteData(route: ActivatedRouteSnapshot): string {
    // construct relative URL from route segments
    const urlSegments = route.pathFromRoot
      .filter(r => r.url.length !== 0)
      .map(r => r.url.map(segment => segment.path).join('/'));

    // replace route parameters with their names
    const parameters = route.paramMap;
    parameters.keys.forEach(key => {
      const paramValue = parameters.get(key);
      if (!paramValue) {
        return;
      }
      if (this.paramFilters.includes(key)) {
        urlSegments.forEach((segment, index) => {
          urlSegments[index] = segment.replace(paramValue, `:${key}`);
        });
      }
    });

    return urlSegments.join('/');
  }

  protected processRouteData(url: string): void {
    // construct absolute URL. This is necessary for Matomo to track the correct UR
    const absoluteUrl = `${window.location.origin}/${url}`;
    this.tracker.setCustomUrl(absoluteUrl);

    // set document title manually for landing page
    if (url === '' || url === '/') {
      this.tracker.setDocumentTitle('Landing page');
    } else {
      this.tracker.setDocumentTitle(url);
    }

    // try to set custom dimensions
    try {
      const lang = getActiveLanguage(this.translaionService);

      if (lang) {
        this.tracker.setCustomDimension(3, lang);
        this.tracker.setCustomDimension(7, lang);
      }
    } catch (error) {
      logging.unknownError(error, { context: 'MatomoInterceptor, set custom dimensions' });
    }
  }
}
